// # Time Management Component for Mentorship
import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import DashboardHeader from '../../components/DashboardHeader';
import DashboardFooter from '../../components/DashboardFooter';
import { Tabs, Tab, Box, Typography, IconButton, TextField, Select, MenuItem, Button } from '@mui/material';
import { PlayArrow, Pause, Stop, Refresh, VolumeOff } from '@mui/icons-material';
import alarmSound from '../../aneo_media/sounds/alarm.wav';
import './neo_time.css';
// # Import Firebase and cache manager
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { useAuthStore } from '../../auth/state';
import cacheManager from '../../auth/cache_manager';

// # Custom TabPanel component
function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`time-tabpanel-${index}`}
      aria-labelledby={`time-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

// # Time formatting utility
const formatTime = (totalSeconds) => {
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
};

// # Time conversion utilities
const convertToSeconds = (value, unit) => {
  switch (unit) {
    case 'hours': return value * 3600;
    case 'minutes': return value * 60;
    case 'seconds': return value;
    default: return value * 60;
  }
};

const convertFromSeconds = (seconds, unit) => {
  switch (unit) {
    case 'hours': return Math.floor(seconds / 3600);
    case 'minutes': return Math.floor(seconds / 60);
    case 'seconds': return seconds;
    default: return Math.floor(seconds / 60);
  }
};

function NeoTime({ stepType }) {
  // # State management
  const [activeTab, setActiveTab] = useState(0);
  const [timerTime, setTimerTime] = useState(parseInt(localStorage.getItem('timerTime')) || 1800);
  const [stopwatchTime, setStopwatchTime] = useState(parseInt(localStorage.getItem('stopwatchTime')) || 0);
  const [isTimerRunning, setIsTimerRunning] = useState(false);
  const [isStopwatchRunning, setIsStopwatchRunning] = useState(false);
  const [inputValue, setInputValue] = useState(30);
  const [inputUnit, setInputUnit] = useState('minutes');
  const [audio] = useState(new Audio(alarmSound));
  const [isAlarmPlaying, setIsAlarmPlaying] = useState(false);
  // # Add dark theme state
  const [isDarkMode, setIsDarkMode] = useState(false);
  const { user } = useAuthStore();

  // # Load theme preference from user data on initial render
  useEffect(() => {
    const loadThemePreference = async () => {
      if (user && user.uid) {
        // # Try to get from cache first
        const userData = cacheManager.get(`user_${user.uid}`);
        if (userData && userData.themes && userData.themes.gen_theme !== undefined) {
          setIsDarkMode(userData.themes.gen_theme === 'dark');
        }
      }
    };
    
    loadThemePreference();
  }, [user]);

  // # Handle theme toggle
  const handleThemeToggle = async () => {
    try {
      const newTheme = !isDarkMode;
      setIsDarkMode(newTheme);
      
      // # Update theme in Firebase if user is logged in
      if (user && user.uid) {
        // # Update the user document
        await updateDoc(doc(db, 'users', user.uid), {
          'themes.gen_theme': newTheme ? 'dark' : 'light'
        });
        
        // # Update cache manager
        const userData = cacheManager.get(`user_${user.uid}`);
        if (userData) {
          const updatedUserData = {
            ...userData,
            themes: {
              ...(userData.themes || {}),
              gen_theme: newTheme ? 'dark' : 'light'
            }
          };
          cacheManager.set(`user_${user.uid}`, updatedUserData);
        }
      }
    } catch (error) {
      console.error('Error updating theme preference:', error);
    }
  };

  // # Cleanup function
  useEffect(() => {
    return () => {
      setIsTimerRunning(false);
      setIsStopwatchRunning(false);
      localStorage.removeItem('timerTime');
      localStorage.removeItem('stopwatchTime');
      localStorage.removeItem('isTimerRunning');
      localStorage.removeItem('isStopwatchRunning');
      audio.pause();
      audio.currentTime = 0;
    };
  }, [audio]);

  // # Timer alarm functions
  const playAlarm = useCallback(() => {
    audio.currentTime = 0;
    audio.play()
      .then(() => {
        setIsAlarmPlaying(true);
        setTimeout(() => {
          audio.pause();
          audio.currentTime = 0;
          setIsAlarmPlaying(false);
        }, 4000);
      })
      .catch(error => console.log('Error playing sound:', error));
  }, [audio]);

  const stopAlarm = useCallback(() => {
    audio.pause();
    audio.currentTime = 0;
    setIsAlarmPlaying(false);
  }, [audio]);

  // # Timer effect
  useEffect(() => {
    let interval;
    if (isTimerRunning && timerTime > 0) {
      interval = setInterval(() => {
        setTimerTime(prev => {
          if (prev <= 1) {
            setIsTimerRunning(false);
            playAlarm();
            // # Add pulse animation class when timer ends
            const display = document.querySelector('.mnt_time_display');
            if (display) {
              display.classList.add('time-end-pulse');
              setTimeout(() => display.classList.remove('time-end-pulse'), 1000);
            }
            return 0;
          }
          // # Add subtle flash animation on each second in dark mode
          if (isDarkMode) {
            const display = document.querySelector('.mnt_time_display');
            if (display) {
              display.classList.add('time-tick');
              setTimeout(() => display.classList.remove('time-tick'), 100);
            }
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [isTimerRunning, playAlarm, isDarkMode]);

  // # Stopwatch effect with animation
  useEffect(() => {
    let interval;
    if (isStopwatchRunning) {
      interval = setInterval(() => {
        setStopwatchTime(prev => {
          // # Add subtle flash animation on each second in dark mode
          if (isDarkMode) {
            const display = document.querySelector('.mnt_time_display');
            if (display) {
              display.classList.add('time-tick');
              setTimeout(() => display.classList.remove('time-tick'), 100);
            }
          }
          return prev + 1;
        });
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [isStopwatchRunning, isDarkMode]);

  // # Persistence effect
  useEffect(() => {
    if (isTimerRunning || isStopwatchRunning) {
      localStorage.setItem('timerTime', timerTime.toString());
      localStorage.setItem('stopwatchTime', stopwatchTime.toString());
    }
  }, [timerTime, stopwatchTime, isTimerRunning, isStopwatchRunning]);

  // # Event handlers
  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleTimerControl = () => {
    setIsTimerRunning(!isTimerRunning);
  };

  const handleStopwatchControl = () => {
    setIsStopwatchRunning(!isStopwatchRunning);
  };

  const resetTimer = () => {
    const newTime = convertToSeconds(inputValue, inputUnit);
    setTimerTime(newTime);
    setIsTimerRunning(false);
    stopAlarm();
  };

  const resetStopwatch = () => {
    setStopwatchTime(0);
    setIsStopwatchRunning(false);
  };

  const handleTimerInput = (event) => {
    const value = Math.max(0, parseInt(event.target.value) || 0);
    setInputValue(value);
    setTimerTime(convertToSeconds(value, inputUnit));
  };

  const handleUnitChange = (event) => {
    const newUnit = event.target.value;
    setInputUnit(newUnit);
    setTimerTime(convertToSeconds(inputValue, newUnit));
  };

  return (
    <div className={`mnt_time_container ${isDarkMode ? 'mnt_time_dark-mode' : ''}`}>
      <DashboardHeader isDarkMode={isDarkMode} onThemeToggle={handleThemeToggle} />
      
      <div className="mnt_time_content">
        <Box sx={{ width: '100%', maxWidth: 800, margin: '0 auto' }}>
          <Typography variant="h4" className="mnt_time_title">
            Time Management - {stepType}
          </Typography>
          
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            centered
            className="mnt_time_tabs"
          >
            <Tab label="Timer" />
            <Tab label="Stopwatch" />
          </Tabs>

          <TabPanel value={activeTab} index={0}>
            <div className="mnt_time_panel">
              <div className="mnt_time_input">
                <TextField
                  type="number"
                  label="Time"
                  value={inputValue}
                  onChange={handleTimerInput}
                  disabled={isTimerRunning}
                  variant="outlined"
                  size="small"
                  sx={{ width: 100, marginRight: 1 }}
                />
                <Select
                  value={inputUnit}
                  onChange={handleUnitChange}
                  disabled={isTimerRunning}
                  size="small"
                  sx={{ width: 120 }}
                >
                  <MenuItem value="seconds">Seconds</MenuItem>
                  <MenuItem value="minutes">Minutes</MenuItem>
                  <MenuItem value="hours">Hours</MenuItem>
                </Select>
              </div>
              <Typography variant="h2" className="mnt_time_display">
                {formatTime(timerTime)}
              </Typography>
              <div className="mnt_time_controls">
                <IconButton 
                  onClick={handleTimerControl}
                  color="primary"
                  size="large"
                >
                  {isTimerRunning ? <Pause /> : <PlayArrow />}
                </IconButton>
                <IconButton 
                  onClick={resetTimer}
                  color="secondary"
                  size="large"
                >
                  <Stop />
                </IconButton>
                <IconButton 
                  onClick={resetTimer}
                  color="default"
                  size="large"
                >
                  <Refresh />
                </IconButton>
              </div>
              {isAlarmPlaying && (
                <Button
                  variant="contained"
                  color="secondary"
                  startIcon={<VolumeOff />}
                  onClick={stopAlarm}
                  className="mnt_time_stop_alarm"
                >
                  Stop Sound
                </Button>
              )}
            </div>
          </TabPanel>

          <TabPanel value={activeTab} index={1}>
            <div className="mnt_time_panel">
              <Typography variant="h2" className="mnt_time_display">
                {formatTime(stopwatchTime)}
              </Typography>
              <div className="mnt_time_controls">
                <IconButton 
                  onClick={handleStopwatchControl}
                  color="primary"
                  size="large"
                >
                  {isStopwatchRunning ? <Pause /> : <PlayArrow />}
                </IconButton>
                <IconButton 
                  onClick={resetStopwatch}
                  color="secondary"
                  size="large"
                >
                  <Stop />
                </IconButton>
                <IconButton 
                  onClick={resetStopwatch}
                  color="default"
                  size="large"
                >
                  <Refresh />
                </IconButton>
              </div>
            </div>
          </TabPanel>
        </Box>
      </div>

      <DashboardFooter isDarkMode={isDarkMode} />
    </div>
  );
}

export default NeoTime;
